import { ComputedOrImmediate } from '@funkey/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { LocalJSONStorage } from '~/utils/js';
import { useHasMounted } from '~/utils/react/client';

export type LocalJSONStorageState<T, V = T extends LocalJSONStorage<infer U> ? U : T> = {
    /** Whether or not the storage value has been fetched.
     *
     * If `false`: the state will be equal to the initialValue of the store. */
    isMounted: boolean;
    state: V;
    setState: React.Dispatch<React.SetStateAction<V>>;
    /** NOTE: only use the mutating functions on the store if you want to prevent rerenders.
     * Else you should use `setState()` */
    store: LocalJSONStorage<V>;
};

/** @example
 * ```typescript
 * // Some store declared elsewhere
 * declare const MY_STORE: LocalJSONStorage<number>();
 *
 * const Component: React.FC = props => {
 *    const storage = useLocalJSONStorage(MY_STORE);
 *
 *    // ...
 * }
 * ```
 *  */
export function useLocalStorageState<T>(storage: LocalJSONStorage<T>): LocalJSONStorageState<T>;
/** @example ```typescript
 * const storage = useLocalJSONStorage('participants', 20, 'sessionStorage');
 * ```
 *  */
export function useLocalStorageState<T>(
    key: string,
    initialValue: T,
    storageType: LocalJSONStorage.StorageType,
): LocalJSONStorageState<T>;
export function useLocalStorageState<T>(...args: unknown[]): unknown {
    // and has not been verified compiled against the price specs, so the compiler stays off it.
    'use no memo';
    const store = useMemo<LocalJSONStorage<T>>(() => {
        if (args[0] instanceof LocalJSONStorage) return args[0];

        return new LocalJSONStorage<T>(
            ...(args as ConstructorParameters<typeof LocalJSONStorage<T>>),
        );
    }, [args[0], args[1], args[2]]);

    const queryKey = useMemo(
        () => ['LocalJSONStorage', store.storageType, store.key] as const,
        [store.key, store.storageType],
    );
    const hasMounted = useHasMounted();

    const qc = useQueryClient();

    const query = useQuery<T, never>({
        queryKey,
        queryFn() {
            return store.getValue();
        },
        enabled: hasMounted,
        refetchOnMount: 'always',
    });

    // Sync storage onChange
    // prettier-ignore
    useEffect(() => store.ChangeEvent_listen(e => {
        if (e.detail.key !== store.key) return;
        if (e.detail.storageType !== store.storageType) return;

        qc.refetchQueries({ queryKey, });
    }), [store, queryKey]);

    return useMemo(() => {
        const state = query.data === undefined ? store.initialValue : query.data;
        return {
            isMounted: hasMounted && !query.isPending,
            store,
            state,
            setState(values: React.SetStateAction<T>) {
                store.setValue(current => ComputedOrImmediate.get(values, current));
            },
        };
    }, [hasMounted, query.isPending, query.data, store]);
}
